import React, { useCallback, useEffect, useState } from 'react'
import { Box, Checkbox, FormControlLabel, Grid, Paper, Theme, Typography, } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useTranslation } from 'react-i18next'
import TextFieldComponent from '@professocial/professocialFormComponents/FormComponents/TextFieldComponent'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import SimpleRichTextEditor from './SimpleRichTextEditor'
import { useDispatch, useSelector } from 'react-redux'
import { getActiveAnnouncement } from '../../../store/common/common.selectors'
import { getAnnouncementTemplateInitiated, updateAnnouncementInitiated, } from '../../../store/common/common.actions'
import { DateTimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import deLocale from 'date-fns/locale/de'
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AnnouncementRequest } from '@professocialApi/professocial-core-frontend-api/models/AnnouncementRequest'
import { UpdateCurrentAnnouncementRequest } from '@professocialApi/professocial-core-frontend-api/apis/AnnouncementApi'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    minWidth: '300px',
    width: '100%',
    margin: theme.spacing(3),
    padding: theme.spacing(4),
  },
  inputField: {
    width: '100%',
    marginTop: theme.spacing(3),
  },
  checkboxContainer: {
    marginTop: theme.spacing(3),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  boxItem: {
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
    padding: theme.spacing(1),
    minWidth: '255px',
    maxWith: '25%',
  },
  sectionLabel: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontWeight: 400,
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}))

const EditorAnnouncement: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const storeAnnouncement = useSelector(getActiveAnnouncement) // get the current template for editing

  const dispatchGetAnnouncementTemplate = useCallback(() => {
    dispatch(getAnnouncementTemplateInitiated())
  }, [dispatch])

  const dispatchUpdateAnnouncement = useCallback((params: UpdateCurrentAnnouncementRequest) => {
    dispatch(updateAnnouncementInitiated(params))
  }, [dispatch])

  const [dateFrom, setDateFrom] = useState<Date>(new Date())
  const [dateTo, setDateTo] = useState<Date>(new Date())

  const [headline, setHeadline] = useState<string | undefined>(storeAnnouncement?.headline)
  const [copyText, setCopyText] = useState<string | undefined>(storeAnnouncement?.copyText)
  const [linkUrl, setLinkUrl] = useState<string | undefined>(storeAnnouncement?.linkUrl)
  const [linkText, setLinkText] = useState<string | undefined>(storeAnnouncement?.linkText)
  const [ctaButtonLabel, setCtaButtonLabel] = useState<string | undefined>(storeAnnouncement?.ctaButtonLabel)
  const [headerText, setHeaderText] = useState<string | undefined>(storeAnnouncement?.headerText)

  const [isHeaderVisible, setIsHeaderVisible] = useState<boolean>(storeAnnouncement?.isHeaderVisible ?? false)

  // Get HTML content length (including tags)
  const getHtmlLength = (html: string | undefined): number => {
    return html?.length || 0
  }

  useEffect(() => {
    dispatchGetAnnouncementTemplate()
  }, [dispatchGetAnnouncementTemplate])

  useEffect(() => {
    setHeadline(storeAnnouncement?.headline)
    setCopyText(storeAnnouncement?.copyText)
    setLinkUrl(storeAnnouncement?.linkUrl)
    setLinkText(storeAnnouncement?.linkText)
    setCtaButtonLabel(storeAnnouncement?.ctaButtonLabel)
    setHeaderText(storeAnnouncement?.headerText)
    setIsHeaderVisible(storeAnnouncement?.isHeaderVisible ?? false)
    setDateFrom(new Date(storeAnnouncement?.start ?? Date.now()))
    setDateTo(new Date(storeAnnouncement?.end ?? Date.now()))
  }, [storeAnnouncement])

  // onChange for the start DateTimePicker
  const handleStartDateChange = (newValue: Date | null) => {
    if (newValue && !isNaN(newValue.getTime())) {
      // Ensure the start date is not in the past
      const now = new Date()
      const updatedStart = newValue.getTime() < now.getTime() ? now : newValue
      setDateFrom(updatedStart)
      // If the current end is before the new start, adjust it accordingly
      if (dateTo.getTime() < updatedStart.getTime()) {
        setDateTo(updatedStart)
      }
    }
  }

  // onChange for the end DateTimePicker
  const handleEndDateChange = (newValue: Date | null) => {
    if (newValue && !isNaN(newValue.getTime())) {
      // Ensure the end date is not before the start date
      const updatedEnd = newValue.getTime() < dateFrom.getTime() ? dateFrom : newValue
      setDateTo(updatedEnd)
    }
  }

  const handleClickSave = () => {
    const template : AnnouncementRequest = {
      headline: headline,
      copyText: copyText,
      start: dateFrom.getTime(),
      end: dateTo.getTime(),
      linkUrl: linkUrl,
      linkText: linkText,
      ctaButtonLabel: ctaButtonLabel,
      isHeaderVisible: isHeaderVisible,
      headerText: headerText,
    }

    const request : UpdateCurrentAnnouncementRequest = {
      announcementRequest: template,
    }

    dispatchUpdateAnnouncement(request)
  }

  return (
    <Paper className={classes.root}>
      <form>
        <Typography variant="h6">{t('announcement:editorTitle')}</Typography>
        <TextFieldComponent
          className={classes.inputField}
          id="headline"
          variant="outlined"
          label={t('announcement:headline')}
          value={headline}
          onChange={(event) => setHeadline(event.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />

        {/* Rich text editor for copyText */}
        <SimpleRichTextEditor
          className={classes.inputField}
          id="copyText"
          label={t('announcement:copyText')}
          value={copyText || ''}
          onChange={(html) => setCopyText(html)}
          maxLength={500}
          helperText={`${getHtmlLength(copyText)}/500`}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={deLocale}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4}>
              <DateTimePicker
                label={t('announcement:start')}
                value={dateFrom}
                onChange={handleStartDateChange}
                slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
                viewRenderers={{
                  hours: renderTimeViewClock,
                  minutes: renderTimeViewClock,
                  seconds: renderTimeViewClock,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <DateTimePicker
                label={t('announcement:end')}
                value={dateTo}
                minDateTime={new Date()}
                onChange={handleEndDateChange}
                slotProps={{ textField: { variant: 'outlined', size: 'small' } }}
                viewRenderers={{
                  hours: renderTimeViewClock,
                  minutes: renderTimeViewClock,
                  seconds: renderTimeViewClock,
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
        <TextFieldComponent
          className={classes.inputField}
          id="linkUrl"
          variant="outlined"
          label={t('announcement:linkUrl')}
          value={linkUrl}
          onChange={(event) => setLinkUrl(event.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextFieldComponent
          className={classes.inputField}
          id="linkText"
          variant="outlined"
          label={t('announcement:linkText')}
          value={linkText}
          onChange={(event) => setLinkText(event.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <TextFieldComponent
          className={classes.inputField}
          id="ctaButtonLabel"
          variant="outlined"
          label={t('announcement:ctaButtonLabel')}
          value={ctaButtonLabel}
          onChange={(event) => setCtaButtonLabel(event.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />

        {/* Optional hint text under CTA Button Label */}
        <Typography className={classes.sectionLabel}>
          {t('announcement:ctaButtonHint', 'Optional: Bleibt dieses Feld leer, so wird auch kein CTA-Button eingeblendet und der Nutzer kann die Benachrichtigung nicht wegklicken.')}
        </Typography>

        <Box className={classes.checkboxContainer}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isHeaderVisible}
                onChange={() => setIsHeaderVisible(!isHeaderVisible)}
                color="primary"
              />
            }
            label={t('announcement:isHeaderVisible')}
          />
        </Box>

        {/* Rich text editor for headerText */}
        <SimpleRichTextEditor
          className={classes.inputField}
          id="headerText"
          label={t('announcement:headerText')}
          value={headerText || ''}
          onChange={(html) => setHeaderText(html)}
          maxLength={500}
          helperText={`${getHtmlLength(headerText)}/500`}
          disabled={!isHeaderVisible}
        />

        <Box className={classes.buttonContainer}>
          <ButtonComponent
            text={t('announcement:saveChanges')}
            onClick={handleClickSave}
            disabled={(headline?.trim().length ?? 0) === 0}
          />
        </Box>
      </form>
    </Paper>
  )
}

export default EditorAnnouncement
